<template>
  <div>
    <modal
      :show.sync="show"
      body-classes="p-0"
      :modal-classes="`modal-xl ${$style.modalClasses}`">
      <card class="card-form mb-0">
        <template slot="header">
          <h2 class="modal-title">Add Manifest</h2>
        </template>
        <el-divider content-position="left">Details</el-divider>
        <ValidationObserver v-slot="{ invalid, validate, pristine }">
          <form
            class="mt-3"
            @submit.prevent="validate().then(addManifest)"
            autocomplete="off">
            <div class="row">
              <div class="col-4">
                <label>Manufacturer</label>
                <el-tooltip
                  manual
                  effect="light"
                  :value="missingManufacturer">
                  <el-select
                    class="select-primary"
                    filterable
                    remote
                    :remote-method="searchManufacturers"
                    :loading="loadingManufacturerResults"
                    v-model="selectedManufacturerID"
                    placeholder="Select manufacturer...">
                    <el-option
                      class="select-primary"
                      v-for="manufacturer in manufacturers"
                      :key="manufacturer.id"
                      :label="manufacturer.name"
                      :value="manufacturer.id" />
                  </el-select>
                  <label
                    slot="content"
                    class="error">Please select a manufacturer.</label>
                </el-tooltip>
              </div>
              <div class="col-4">
                <ValidationProvider
                  name="Job Name"
                  rules="required"
                  v-slot="{ errors }">
                  <base-input
                    name="jobName"
                    type="text"
                    label="Job Name"
                    v-model="manifest.jobName"
                    :error="errors[0]" />
                </ValidationProvider>
              </div>
              <div class="col-4">
                <ValidationProvider
                  name="Status"
                  rules="required"
                  v-slot="{ errors }">
                  <label>Status</label>
                  <div class="form-group">
                    <el-select
                      class="select-primary"
                      v-model="manifest.status"
                      placeholder="Select status">
                      <el-option
                        class="select-primary"
                        v-for="option in statusOptions"
                        :key="option"
                        :label="option"
                        :value="option" />
                    </el-select>
                    <label class="error">{{ errors[0] }}</label>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="row">
              <div class="col-4">
                <ValidationProvider
                  name="Departure Date"
                  rules="required"
                  v-slot="{ errors }">
                  <base-input
                    name="departureDate"
                    type="date"
                    label="Departure Date"
                    :error="errors[0]">
                    <el-date-picker
                      :picker-options="{ disabledDate: isDepartureDateSelectionInvalid }"
                      format="MM/dd/yyyy"
                      v-model="manifest.departureDate"
                      type="date"
                      placeholder="Select date" />
                  </base-input>
                </ValidationProvider>
              </div>
              <div class="col-4">
                <ValidationProvider
                  name="Arrival Date"
                  rules="required"
                  v-slot="{ errors }">
                  <base-input
                    name="arrivalDate"
                    type="date"
                    label="Arrival Date"
                    :error="errors[0]">
                    <el-date-picker
                      :picker-options="{ disabledDate: isArrivalDateSelectionInvalid }"
                      format="MM/dd/yyyy"
                      v-model="manifest.arrivalDate"
                      type="date"
                      placeholder="Select date" />
                  </base-input>
                </ValidationProvider>
              </div>
              <div class="col-4">
                <label>Carrier</label>
                <el-select
                  class="select-primary"
                  filterable
                  remote
                  :remote-method="searchCarriers"
                  :loading="loadingCarrierResults"
                  v-model="selectedCarrierID"
                  placeholder="Select carrier...">
                  <el-option
                    class="select-primary"
                    v-for="carrier in carriers"
                    :key="carrier.id"
                    :label="carrier.name"
                    :value="carrier.id" />
                </el-select>
              </div>
            </div>
            <div>
              <base-input
                v-if="showNotesTextArea"
                name="notes"
                label="Notes">
                <textarea
                  v-focus
                  style="max-height: unset;"
                  v-model="manifest.notes"
                  class="form-control"
                  rows="3" />
              </base-input>
              <base-button
                class="pl-0"
                link
                type="primary"
                v-if="!showNotesTextArea"
                @click="showNotesTextArea = true">
                Add Notes
              </base-button>
            </div>
            <div>
              <div style="display: flex;">
                <div class="el-divider el-divider--horizontal">
                  <div class="el-divider__text is-left">Images</div>
                  <div class="el-divider__text is-right">
                    <file-upload
                      :class="$style.fileUpload"
                      class="btn btn-sm btn-primary"
                      ref="upload"
                      v-model="files"
                      :multiple="true"
                      accept="image/*"
                      @input-file="inputFile"
                      @input-filter="inputFilter">
                      <i class="fa fa-upload" />
                      <span class="pl-2">Upload Images</span>
                    </file-upload>
                  </div>
                </div>
              </div>
              <silent-box
                v-if="images.length"
                :gallery="images">
                <template v-slot:silentbox-item="{ silentboxItem }">
                  <div :class="$style.deleteImageContainer">
                    <div :class="$style.deleteImageBanner">
                      <i
                        class="fa fa-trash-alt"
                        @click="(e) => { deleteImage(silentboxItem.id, e) }" />
                    </div>
                    <img
                      :src="silentboxItem.src"
                      width="100px"
                      height="100px">
                  </div>
                </template>
              </silent-box>
              <p
                v-else
                :class="$style.noImagesText">
                No images have been uploaded yet.
              </p>
            </div>
            <div class="mt-5">
              <div style="display: flex;">
                <div class="el-divider el-divider--horizontal">
                  <div class="el-divider__text is-left">Orders</div>
                  <div class="el-divider__text is-right">
                    <base-button
                      size="sm"
                      type="primary"
                      @click="showAddOrderModal = true">
                      Add Order
                    </base-button>
                  </div>
                </div>
              </div>
              <el-table
                max-height="350"
                :data="manifest.orders"
                @row-click="clickedOrder"
                empty-text="No orders have been added yet.">
                <el-table-column
                  v-for="column in tableColumns"
                  :key="column.label"
                  :width="column.width"
                  :min-width="column.minWidth"
                  :prop="column.prop"
                  :label="column.label"
                  :formatter="column.formatter"
                  :show-overflow-tooltip="true" />
                <el-table-column
                  min-width="100"
                  header-align="right"
                  label="Actions">
                  <div
                    slot-scope="{ $index }"
                    class="text-right">
                    <el-tooltip
                      content="Remove order"
                      :open-delay="800"
                      placement="top">
                      <base-button
                        type="primary"
                        size="sm"
                        icon
                        @click="removeOrder($index)">
                        <i class="fa fa-trash-alt" />
                      </base-button>
                    </el-tooltip>
                  </div>
                </el-table-column>
              </el-table>
            </div>
            <el-divider />
            <div :class="$style.actions">
              <base-button
                type="default"
                class="btn-fill"
                @click="$emit('update:show', false)">
                Cancel
              </base-button>
              <div :class="$style.right">
                <base-button
                  id="addManifest"
                  :disabled="invalid || pristine"
                  native-type="submit"
                  type="primary"
                  class="btn-fill">
                  Submit
                </base-button>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </card>
    </modal>
    <new-order
      v-if="showAddOrderModal"
      :show="showAddOrderModal"
      :job-name="manifest.jobName"
      @update:show="showAddOrderModal = $event;"
      @addOrder="addOrder" />
    <edit-order
      v-if="showEditOrderModal"
      :show="showEditOrderModal"
      :job-name="manifest.jobName"
      :order="selectedOrder"
      @update:show="showEditOrderModal = $event;"
      @updateOrder="updateOrder" />
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment-timezone';
import FileUpload from 'vue-upload-component';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Modal } from 'src/components/UI';
import NewOrder from 'src/modules/Manifest/NewOrder';
import EditOrder from 'src/modules/Manifest/EditOrder';
import {
  DatePicker,
  Divider,
} from 'element-ui';

export default {
  name: 'NewManifest',
  components: {
    Modal,
    FileUpload,
    [DatePicker.name]: DatePicker,
    [Divider.name]: Divider,
    ValidationObserver,
    ValidationProvider,
    NewOrder,
    EditOrder,
  },
  props: {
    manufacturers: {
      type: Array,
      required: true,
    },
    carriers: {
      type: Array,
      required: true,
    },
    images: {
      type: Array,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    statusOptions: {
      type: Array,
      required: true,
    },
    departureDate: {
      type: Date,
      required: true,
    },
    arrivalDate: {
      type: Date,
      required: true,
    },
    loadingManufacturerResults: {
      type: Boolean,
      required: true,
    },
    loadingCarrierResults: {
      type: Boolean,
      required: true,
    },
  },
  created() {
    this.manifest = { ...this.emptyManifest };
  },
  data() {
    return {
      files: [],
      manifest: {},
      selectedManufacturerID: null,
      selectedCarrierID: null,
      missingManufacturer: false,
      emptyManifest: {
        orders: [],
        departureDate: this.departureDate,
        arrivalDate: this.arrivalDate,
      },
      showNotesTextArea: false,
      showAddOrderModal: false,
      showEditOrderModal: false,
      selectedOrder: null,
      tableColumns: [
        {
          prop: 'orderNumber',
          label: 'Order No.',
        },
        {
          prop: 'purchaseOrder',
          label: 'PO. No.',
        },
        {
          prop: 'status',
          label: 'Status',
        },
      ],
    };
  },
  methods: {
    deleteImage(id, event) {
      event.preventDefault();
      event.stopPropagation();
      this.$emit('deleteImage', id);
    },
    isDepartureDateSelectionInvalid(date) {
      const today = new Date(new Date().setHours(0, 0, 0, 0));
      return today > date;
    },
    isArrivalDateSelectionInvalid(date) {
      return date < new Date(this.manifest.departureDate);
    },
    removeOrder(index) {
      this.manifest.orders.splice(index, 1);
    },
    addManifest() {
      if (!this.selectedManufacturerID) {
        this.missingManufacturer = true;
      }
      if (!this.missingManufacturer) {
        this.$emit('addManifest', {
          ...this.manifest,
          manufacturerID: this.selectedManufacturerID,
          ...(this.selectedCarrierID && { carrierID: this.selectedCarrierID }),
        });
      }
    },
    inputFile(newFile) {
      if (newFile) {
        this.$emit('uploadDocument', newFile);
      }
    },
    inputFilter(newFile) {
      // Create a blob field
      newFile.blob = '';
      const URL = window.URL || window.webkitURL;
      if (URL?.createObjectURL) {
        newFile.blob = URL.createObjectURL(newFile.file);
      }
    },
    addOrder(order) {
      this.manifest.orders.push(order);
    },
    clickedOrder(order) {
      this.selectedOrder = { ...order };
      this.showEditOrderModal = true;
    },
    updateOrder(order) {
      const index = this.manifest.orders.findIndex(o => o.orderNumber === order.orderNumber);
      Vue.set(this.manifest.orders, index, order);
    },
    searchManufacturers(search) {
      this.$emit('searchManufacturers', search);
    },
    searchCarriers(search) {
      this.$emit('searchCarriers', search);
    },
    formatDate(date) {
      if (date) {
        return moment(date).format('MM/DD/YYYY');
      }
      return '';
    },
  },
  watch: {
    selectedManufacturerID(val) {
      if (val) this.missingManufacturer = false;
    },
  },
};
</script>

<style module lang="scss">
@import 'assets/sass/dashboard/custom/_variables.scss';

.noImagesText {
  text-align: center;
  color: #909399!important;
}
.fileUpload {
  width: 220px;
  display: flex!important;
  justify-content: center;
  align-items: center;
}
.fileUpload label {
  cursor: pointer;
}
.deleteImageContainer {
  position: relative;
}
.deleteImageContainer:hover {
  .deleteImageBanner {
    display: flex;
  }
}
.deleteImageBanner {
  display: none;
  cursor: default;
  position: absolute;
  bottom: 0px;
  padding: 4px;
  background: rgba(0,0,0,0.7);
  border-radius: 0px 0px 4px 4px;
  width: 100%;
  justify-content: flex-end;
  height: 24px;
  z-index: 100;
  align-items: center;
  i {
    cursor: pointer;
    color: red;
  }
}
.modalClasses {
  min-width: 750px;
}
.actions {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
}
.actions .right > *{
  margin-right: 10px;
  margin-left: 10px;
}
.link {
  font-weight: 400;
  padding: 0px;
  margin: 0px;
  margin-left: 3px;
  line-height: 1.5;
}
.sectionTitle {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}
</style>

<style>
.el-select-dropdown__item {
  height: auto!important;
}
.el-tooltip__popper label {
  margin-bottom: 0px;
}
</style>
